import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { FaEnvelope, FaPhone, FaMapMarkerAlt, FaGithub, FaInstagram } from 'react-icons/fa';

const Contact = () => {
  const [sectionRef, inView] = useInView({
    triggerOnce: false,
    threshold: 0.1,
  });

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });

  const [submitting, setSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submitError, setSubmitError] = useState(false);

  const sectionVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: 'easeOut',
        when: 'beforeChildren',
        staggerChildren: 0.2
      }
    }
  };

  const titleVariants = {
    hidden: { opacity: 0, y: -20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.5, ease: 'easeOut' }
    }
  };

  const cardVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.5, ease: 'easeOut' }
    }
  };

  const formItemVariants = {
    hidden: { opacity: 0, x: -20 },
    visible: {
      opacity: 1,
      x: 0,
      transition: { duration: 0.3, ease: 'easeOut' }
    }
  };

  const contactInfoVariants = {
    hidden: { opacity: 0, x: 20 },
    visible: {
      opacity: 1,
      x: 0,
      transition: { duration: 0.3, ease: 'easeOut' }
    }
  };

  const buttonVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.5, ease: 'easeOut' }
    },
    hover: {
      scale: 1.05,
      boxShadow: '0 0 20px rgba(14, 165, 233, 0.5)',
      transition: { duration: 0.3, type: 'spring', stiffness: 400 }
    },
    tap: { scale: 0.95 }
  };

  const waveVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        when: 'beforeChildren',
        staggerChildren: 0.1
      }
    }
  };

  const wavePath = {
    hidden: { opacity: 0, pathLength: 0 },
    visible: {
      opacity: 1,
      pathLength: 1,
      transition: { duration: 1.5, ease: 'easeInOut' }
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setSubmitting(true);
    
    // 模擬表單提交
    setTimeout(() => {
      setSubmitting(false);
      setSubmitSuccess(true);
      
      // 重置表單
      setFormData({
        name: '',
        email: '',
        subject: '',
        message: ''
      });
      
      // 清除成功消息
      setTimeout(() => {
        setSubmitSuccess(false);
      }, 5000);
    }, 2000);
  };

  return (
    <section id="contact" className="py-20 relative">
      {/* 背景裝飾 - 波浪效果 */}
      <div className="absolute top-0 left-0 w-full">
        <motion.svg
          width="100%"
          height="120"
          viewBox="0 0 1440 120"
          preserveAspectRatio="none"
          variants={waveVariants}
          initial="hidden"
          animate={inView ? "visible" : "hidden"}
        >
          <motion.path
            d="M0,64L60,80C120,96,240,128,360,128C480,128,600,96,720,80C840,64,960,64,1080,69.3C1200,75,1320,85,1380,90.7L1440,96L1440,0L1380,0C1320,0,1200,0,1080,0C960,0,840,0,720,0C600,0,480,0,360,0C240,0,120,0,60,0L0,0Z"
            fill="rgba(15, 23, 42, 1)"
            variants={wavePath}
          />
          <motion.path
            d="M0,64L60,69.3C120,75,240,85,360,85.3C480,85,600,75,720,69.3C840,64,960,64,1080,69.3C1200,75,1320,85,1380,90.7L1440,96L1440,0L1380,0C1320,0,1200,0,1080,0C960,0,840,0,720,0C600,0,480,0,360,0C240,0,120,0,60,0L0,0Z"
            fill="rgba(14, 165, 233, 0.1)"
            variants={wavePath}
          />
        </motion.svg>
      </div>

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <motion.div
          ref={sectionRef}
          variants={sectionVariants}
          initial="hidden"
          animate={inView ? 'visible' : 'hidden'}
        >
          <motion.div className="text-center mb-16" variants={titleVariants}>
            <motion.h2 
              variants={titleVariants} 
              className="text-3xl md:text-4xl font-bold mb-4 inline-block"
            >
              聯絡<span className="text-gradient">我</span>
            </motion.h2>
            <motion.div variants={titleVariants} className="w-20 h-1 bg-gradient-to-r from-primary-500 to-secondary-500 mx-auto mb-8"></motion.div>
            <motion.p variants={titleVariants} className="text-lg text-gray-300 max-w-2xl mx-auto">
              有任何問題或合作機會嗎？請隨時與我聯繫，我很期待您的來信！
            </motion.p>
          </motion.div>

          <div className="max-w-2xl mx-auto">
            <div className="grid gap-6 sm:gap-8">
              {/* 聯絡表單 */}
              

              {/* 聯絡資訊 */}
              <motion.div variants={contactInfoVariants} className="flex flex-col gap-6">
                <motion.div 
                  variants={cardVariants}
                  whileHover={{ scale: 1.02 }}
                  className="card"
                >
                  <h3 className="text-xl font-semibold mb-6 text-primary-300">聯絡資訊</h3>
                  
                  <div className="space-y-4 sm:space-y-6">
                    <div className="flex items-start gap-4">
                      <div className="bg-primary-900/40 text-primary-400 p-3 rounded-lg">
                        <FaEnvelope className="text-xl" />
                      </div>
                      <div>
                        <h4 className="text-lg font-medium mb-1 text-white">電子郵件</h4>
                        <p className="text-gray-300">karta2398980@gmail.com</p>
                      </div>
                    </div>

                    <div className="flex items-start gap-4">
                      <div className="bg-primary-900/40 text-primary-400 p-3 rounded-lg">
                        <FaPhone className="text-xl" />
                      </div>
                      <div>
                        <h4 className="text-lg font-medium mb-1 text-white">電話</h4>
                        <p className="text-gray-300">0909997970</p>
                      </div>
                    </div>

                    <div className="flex items-start gap-4">
                      <div className="bg-primary-900/40 text-primary-400 p-3 rounded-lg">
                        <FaMapMarkerAlt className="text-xl" />
                      </div>
                      <div>
                        <h4 className="text-lg font-medium mb-1 text-white">位置</h4>
                        <p className="text-gray-300">台灣的某處</p>
                      </div>
                    </div>
                  </div>
                </motion.div>

                <motion.div 
                  variants={cardVariants}
                  whileHover={{ scale: 1.02 }}
                  className="card"
                >
                  <h3 className="text-xl font-semibold mb-6 text-primary-300">社群連結</h3>
                  
                  <div className="flex gap-3 sm:gap-4">
                    <motion.a
                      href="https://github.com/kaikaichumi"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="bg-slate-800 hover:bg-primary-600 w-12 h-12 rounded-full flex items-center justify-center text-white transition-colors duration-300"
                      whileHover={{ scale: 1.1, rotate: 10 }}
                      whileTap={{ scale: 0.9 }}
                    >
                      <FaGithub className="text-xl" />
                    </motion.a>
                    
                    <motion.a
                      href="https://www.instagram.com/dorapen_photo"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="bg-slate-800 hover:bg-primary-600 w-12 h-12 rounded-full flex items-center justify-center text-white transition-colors duration-300"
                      whileHover={{ scale: 1.1, rotate: 10 }}
                      whileTap={{ scale: 0.9 }}
                    >
                      <FaInstagram className="text-xl" />
                    </motion.a>
                  </div>
                </motion.div>

                {/* 地圖或其他裝飾 */}
                <motion.div 
                  variants={cardVariants}
                  className="card flex-1 flex items-center justify-center"
                >
                  <div className="text-center">
                    <div className="text-6xl mb-3">🌏</div>
                    <p className="text-gray-300">期待與您合作！</p>
                  </div>
                </motion.div>
              </motion.div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Contact;
